// ============================================================
// HR module manifest — R3 retrofit (2026-04-24).
//
// Owns employee master + taxonomies (designations, departments,
// component types) + photo storage. Foundation module for
// Leave + Payroll (both depend on it via the registry validator).
//
// Cross-module exports surface in index.js: subscribeEmployees,
// fetchEmployeeByAuthEmail, fetchMyEmployee — these are read by
// leave-management, my-profile, my-payslips, generate-payslip.
// ============================================================

export default {
  id:          'hr',
  name:        'HR',
  version:     '1.0.0',
  description: 'Employee master + designations/departments/component-types taxonomies + photo storage.',

  load: () => import('./index.js'),

  routes: [
    {
      id:       'employees',
      label:    'Employees',
      icon:     '👥',
      section:  'people',
      sidebarPosition: 10,
      // 2026-05-04 sidebar-leak fix — gate on literal `hr.read`.
      // Roles granted: director ('*' fast-path), admin (__ALL_EXCEPT_TENANT_CONFIG__),
      // hr (hr.* wildcard). Staff / shop-floor / production-supervisor /
      // sales / consultant do NOT see Employees. Per-field PII redaction
      // (BasicSalary, Bank, EPF, SOCSO, Tax) is gated separately by
      // hr.payroll-fields.read inside the page.
      // NOTE: this widens visibility from the prior `director|hr` literal
      // to include admin too — consistent with R6's admin = everything
      // operational. If admin should NOT see Employees, raise a design
      // debate in DESIGN_LOG and re-tighten with a !hr.read negation in
      // industry-pack admin role.
      visible:  (role, hasPerm) => hasPerm('hr.read'),
      mount:    'mountEmployees',
      unmount:  'unmountEmployees'
    }
  ],

  schemas: [
    'employees',
    'designations',
    'departments',
    'componenttypes'   // shared with inventory historically; HR owns the editor
  ],

  permissions: [
    'hr.read',                  // see employee list (HR + director)
    'hr.write',                 // create / edit employees
    'hr.archive',               // soft-delete
    'hr.delete',                // permanent delete
    'hr.payroll-fields.read',   // see BasicSalary, Bank, EPF, SOCSO, Tax (HR + director only)
    'hr.taxonomies.write'       // edit designations / departments / component types
  ],

  // Foundation module — no deps. Leave + Payroll depend on hr.
  dependencies: [],

  fitsIndustries: ['manufacturing', 'trading', 'services', 'retail'],

  functions: [],

  // Dashboard widgets — HR block (only for HR + director roles).
  // Composed into the dedicated 'hr-block' section of the dashboard host.
  dashboardWidgets: [
    {
      id:           'hr-active-employees-kpi',
      label:        'Active employees',
      section:      'hr-block',
      order:        10,
      defaultRoles: ['director', 'hr']
    },
    {
      id:           'hr-birthdays-this-month-kpi',
      label:        'Birthdays this month',
      section:      'hr-block',
      order:        40,
      defaultRoles: ['director', 'hr']
    },
    {
      id:           'hr-anniversaries-this-month-kpi',
      label:        'Anniversaries this month',
      section:      'hr-block',
      order:        50,
      defaultRoles: ['director', 'hr']
    }
  ]
};
